import * as React from "react";
import { Button, Text, Tooltip } from "rizzui";
import { PiCopySimple, PiChecksBold } from "react-icons/pi";
import { useCopyToClipboard } from "@/hooks/use-copy-to-clipboard";
import cn from "@/utils/class-names";
import LandingPagesRepository from "@/utilities/repositories/LandingPages";
import { BiRefresh } from "react-icons/bi";
import { useNavigate } from "react-router-dom";
import { routes } from "@/config/routes";
import ModalWrapper from "./components/ModalWrapper";
import { ICheckCNames } from "@/utilities/types/LandingPage";


export interface DnsDomainConfigurationGuideProps {
  cNameTargetDomain?: string;
  cNameTarget?: string;
  customDomain: string;
  onDone: () => void;
  isOpen: boolean;
  onBack?: () => void;
  onRemove?: (id: string) => void;
  id?: string;
}

const getSubdomainHost = (domain: string) => {
  if (!domain) return null;
  const parts = domain.split(".");
  if (parts.length < 3) return null;

  const tld = parts[parts.length - 1];
  const sld = parts[parts.length - 2];
  const secondLevelTlds = [
    "com",
    "co",
    "net",
    "org",
    "gov",
    "edu",
    "ac",
    "mil",
  ];

  let rootPartsCount = 2;
  // Check for ccTLDs with SLDs (e.g., .com.sg, .co.uk)
  if (tld.length === 2 && secondLevelTlds.includes(sld)) {
    rootPartsCount = 3;
  }

  if (parts.length > rootPartsCount) {
    return parts.slice(0, parts.length - rootPartsCount).join(".");
  }
  return null;
};

export default function DnsDomainConfigurationGuide({
  cNameTargetDomain,
  cNameTarget,
  customDomain,
  onDone,
  isOpen,
  onBack,
  onRemove,
  id,
}: DnsDomainConfigurationGuideProps) {
  const navigate = useNavigate();
  const [isCopied, setIsCopied] = React.useState(false);
  const [isACopied, setIsACopied] = React.useState(false);
  const [, copyToClipboard] = useCopyToClipboard();
  const [errorMsg, setErrorMsg] = React.useState<ICheckCNames | undefined>(
    undefined,
  );
  const [loading, setLoading] = React.useState(false);

  const subdomainHost = React.useMemo(
    () => getSubdomainHost(customDomain),
    [customDomain],
  );

  /**
   * Kiểm tra trạng thái kết nối DNS của domain
   */
  const checkDnsStatus = React.useCallback(
    async (domain: string) => {
      if (loading || !domain) return;

      setLoading(true);
      try {
        // await LandingPagesRepository.checkCnameConnection({
        //   domain,
        // });
        const result = await LandingPagesRepository.checkCnamesConnection({
          domain,
        });
        setErrorMsg(result.data);
      } catch (error: any) {
        // setErrorMsg((error as { message?: string })?.message ?? "Not found");
        setErrorMsg({
          cNameTarget:
            typeof error?.message === "string" ? error?.message : "Not found",
          cNameTargetDomain:
            typeof error?.message === "string" ? error?.message : "Not found",
        });
      } finally {
        setLoading(false);
      }
    },
    [loading],
  );

  const handleCopyToClipboard = React.useCallback(
    (value: string) => {
      copyToClipboard(value);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    },
    [copyToClipboard],
  );

  const handleCopyAToClipboard = React.useCallback(
    (value: string) => {
      copyToClipboard(value);
      setIsACopied(true);
      setTimeout(() => setIsACopied(false), 2000);
    },
    [copyToClipboard],
  );

  const handleDone = React.useCallback(() => {
    onDone();
    navigate(routes.landingPages.listing);
  }, [onDone, navigate]);

  //check dns status when modal open
  React.useEffect(() => {
    if (isOpen && customDomain) {
      checkDnsStatus(customDomain);
    }
  }, [isOpen, customDomain]);

  return (
    <ModalWrapper
      isOpen={isOpen}
      onClose={handleDone}
      title="Publish On Your Domain"
      containerClassName="max-w-[90vw] md:max-w-[80vw] xl:max-w-[60vw] w-full mx-auto"
    >
      {/* Domain Info */}
      <div className="mb-6 text-center">
        <Text className="text-gray-500 text-sm mb-2">CONNECTED DOMAIN:</Text>

        <div className="flex justify-center items-end gap-3 cursor-pointer mb-2">
          <Text className="text-xl font-semibold text-indigo-600 leading-[14px]">
            {customDomain}
          </Text>
          <Text
            className="font-semibold text-red-600 leading-[12px]"
            title="Remove"
            onClick={() => onRemove && id && onRemove(id)}
          >
            Remove
          </Text>
        </div>
      </div>

      {/* DNS Settings Table */}
      <div className="mb-6">
        <Text className="font-semibold text-base mb-2 text-center">
          DNS SETTINGS:
        </Text>
        <div className="w-full border rounded-lg overflow-hidden">
          <div className="hidden md:grid grid-cols-8 bg-gray-50 text-xs font-semibold text-gray-600 py-2 px-4 gap-4">
            <div>Host</div>
            <div>Record</div>
            <div className="col-span-4">Required Data</div>
            <div className="col-span-2">Current Data</div>
          </div>
          {/* @ row */}
          <div className="hidden md:grid grid-cols-8 items-center py-3 px-4 border-t text-sm gap-4">
            <div>{subdomainHost || "@"}</div>
            <div>CNAME</div>
            <div className="flex items-center col-span-4 gap-2">
              <Tooltip content={cNameTargetDomain}>
                <span className="truncate">{cNameTargetDomain}</span>
              </Tooltip>
              <Button
                variant="text"
                size="sm"
                onClick={() =>
                  cNameTargetDomain && handleCopyToClipboard(cNameTargetDomain)
                }
                className="h-8 px-2"
              >
                {isCopied ? (
                  <PiChecksBold className="h-5 w-5 text-green-500" />
                ) : (
                  <PiCopySimple className="h-5 w-5" />
                )}
              </Button>
            </div>
            <div
              className={cn(
                "font-medium col-span-2",
                errorMsg?.cNameTargetDomain ? "text-red-500" : "text-green-500",
              )}
            >
              {errorMsg?.cNameTargetDomain
                ? "CNAME record not found"
                : "Connected"}
            </div>
          </div>

          {/* www row */}
          {!subdomainHost && (
            <div className="hidden md:grid grid-cols-8 items-center py-3 px-4 border-t text-sm gap-4">
              <div>www</div>
              <div>CNAME</div>
              <div className="flex items-center col-span-4 gap-2">
                <Tooltip content={cNameTargetDomain}>
                  <span className="truncate">{cNameTargetDomain}</span>
                </Tooltip>
                <Button
                  variant="text"
                  size="sm"
                  onClick={() =>
                    cNameTargetDomain &&
                    handleCopyAToClipboard(cNameTargetDomain)
                  }
                  className="h-8 px-2"
                >
                  {isACopied ? (
                    <PiChecksBold className="h-5 w-5 text-green-500" />
                  ) : (
                    <PiCopySimple className="h-5 w-5" />
                  )}
                </Button>
              </div>
              <div
                className={cn(
                  "font-medium col-span-2",
                  errorMsg?.cNameTarget ? "text-red-500" : "text-green-500",
                )}
              >
                {errorMsg?.cNameTarget ? "CNAME record not found" : "Connected"}
              </div>
            </div>
          )}

          {/* Stacked layout for mobile screens */}
          <div className="md:hidden flex flex-col divide-y">
            {/* @ row */}
            <div className="flex flex-col gap-2 py-3 px-4">
              <div className="flex justify-between text-xs text-gray-600 font-semibold mb-1">
                <span>Host</span>
                <span>{subdomainHost || "@"}</span>
              </div>
              <div className="flex justify-between text-xs text-gray-600 font-semibold mb-1">
                <span>Record</span>
                <span>CNAME</span>
              </div>
              <div className="flex justify-between items-center text-xs text-gray-600 font-semibold mb-1">
                <span>Required Data</span>
                <span className="flex items-center gap-2">
                  <Tooltip content={cNameTargetDomain}>
                    <span className="truncate max-w-[100px]">
                      {cNameTargetDomain}
                    </span>
                  </Tooltip>
                  <Button
                    variant="text"
                    size="sm"
                    onClick={() =>
                      cNameTargetDomain &&
                      handleCopyToClipboard(cNameTargetDomain)
                    }
                    className="h-8 p-0"
                  >
                    {isCopied ? (
                      <PiChecksBold className="h-5 w-5 text-green-500" />
                    ) : (
                      <PiCopySimple className="h-5 w-5" />
                    )}
                  </Button>
                </span>
              </div>
              <div className="flex justify-between text-xs text-gray-600 font-semibold mb-1">
                <span>Current Data</span>
                <span
                  className={cn(
                    "font-medium",
                    errorMsg?.cNameTargetDomain
                      ? "text-red-500"
                      : "text-green-500",
                  )}
                >
                  {errorMsg?.cNameTargetDomain
                    ? "CNAME record not found"
                    : "Connected"}
                </span>
              </div>
            </div>

            {/* www row */}
            {!subdomainHost && (
              <div className="flex flex-col gap-2 py-3 px-4">
                <div className="flex justify-between text-xs text-gray-600 font-semibold mb-1">
                  <span>Host</span>
                  <span>www</span>
                </div>
                <div className="flex justify-between text-xs text-gray-600 font-semibold mb-1">
                  <span>Record</span>
                  <span>CNAME</span>
                </div>
                <div className="flex justify-between items-center text-xs text-gray-600 font-semibold mb-1">
                  <span>Required Data</span>
                  <span className="flex items-center gap-2">
                    <Tooltip content={cNameTargetDomain}>
                      <span className="truncate max-w-[100px]">
                        {cNameTargetDomain}
                      </span>
                    </Tooltip>
                    <Button
                      variant="text"
                      size="sm"
                      onClick={() =>
                        cNameTargetDomain &&
                        handleCopyAToClipboard(cNameTargetDomain)
                      }
                      className="h-8 p-0"
                    >
                      {isACopied ? (
                        <PiChecksBold className="h-5 w-5 text-green-500" />
                      ) : (
                        <PiCopySimple className="h-5 w-5" />
                      )}
                    </Button>
                  </span>
                </div>
                <div className="flex justify-between text-xs text-gray-600 font-semibold mb-1">
                  <span>Current Data</span>
                  <span
                    className={cn(
                      "font-medium",
                      errorMsg?.cNameTarget ? "text-red-500" : "text-green-500",
                    )}
                  >
                    {errorMsg?.cNameTarget
                      ? "CNAME record not found"
                      : "Connected"}
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <Text className="text-xs text-gray-400 text-center mb-6">
        Note: Complete DNS Resolution may take up to 48 hours
      </Text>

      {/* Refresh button */}
      <div className="flex justify-center mb-4">
        <Button
          variant="text"
          className="text-gray-500 text-xs"
          onClick={() => checkDnsStatus(customDomain)}
          disabled={loading}
        >
          Refresh
          <BiRefresh
            className={cn("w-4 h-4 ml-1", loading && "animate-spin")}
          />
        </Button>
      </div>

      {/* Action buttons */}
      <div className="flex justify-between">
        <div>
          {onBack && (
            <Button variant="outline" onClick={onBack}>
              Go Back
            </Button>
          )}
        </div>

        <Button onClick={handleDone}>Done</Button>
      </div>
    </ModalWrapper>
  );
}
